import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "./useApiClient";
import type { NoticeResponse, NoticeUpsertRequest } from "../types";

// ============================================================================
// 取得用フック (Queries)
// ============================================================================

/**
 * ユーザー向け：公開中のお知らせ一覧を取得
 */
export const usePublishedNoticesQuery = () => {
  const apiClient = useApiClient();

  return useQuery<NoticeResponse[], Error>({
    queryKey: ["notices", "published"],
    queryFn: async () => {
      const response = await apiClient.get<NoticeResponse[]>("/Notices/published");
      return response.data;
    },
    // キャッシュ時間を長めにして無駄な通信を抑える
    staleTime: 5 * 60 * 1000, 
  });
};

/**
 * 管理者向け：すべてのお知らせ一覧（予約中含む）を取得
 */
export const useAllNoticesQuery = () => {
  const apiClient = useApiClient();

  return useQuery<NoticeResponse[], Error>({
    queryKey: ["notices", "all"],
    queryFn: async () => {
      const response = await apiClient.get<NoticeResponse[]>("/Notices/all");
      return response.data;
    },
  });
};

// ============================================================================
// 更新用フック (Mutations)
// ============================================================================

/**
 * お知らせの登録
 */
export const useCreateNoticeMutation = () => {
  const queryClient = useQueryClient();
  const apiClient = useApiClient();
  
  return useMutation({
    mutationFn: async (request: NoticeUpsertRequest) => {
      const response = await apiClient.post<NoticeResponse>("/Notices", request);
      return response.data;
    },
    onSuccess: () => {
      // 成功時、関連するキャッシュを破棄して一覧を最新化
      queryClient.invalidateQueries({ queryKey: ["notices"] });
    },
  });
};

/**
 * お知らせの更新
 */
export const useUpdateNoticeMutation = () => {
  const queryClient = useQueryClient();
  const apiClient = useApiClient();
  
  return useMutation({
    mutationFn: async ({ id, request }: { id: number; request: NoticeUpsertRequest }) => {
      await apiClient.put(`/Notices/${id}`, request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notices"] });
    },
  });
};

/**
 * お知らせの削除
 */
export const useDeleteNoticeMutation = () => {
  const queryClient = useQueryClient();
  const apiClient = useApiClient();
  
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/Notices/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notices"] });
    },
  });
};